// components/auth/LoginForm.js - Updated with Remember Me & Extended Sessions
import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// Import only required icons
import { User, Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowRight, Mail, Loader2, Clock, Crown } from 'lucide-react';

// Google OAuth Button Component
const GoogleAuthButton = ({ onClick, loading, disabled, darkMode }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || loading}
      className={`w-full flex items-center justify-center space-x-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 border ${
        darkMode 
          ? 'bg-[#13283C] hover:bg-[#1C3348] text-white border-[#28445F]' 
          : 'bg-white hover:bg-gray-50 text-gray-700 border-gray-300'
      } disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {loading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : (
        <svg className="w-5 h-5" viewBox="0 0 24 24">
          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
        </svg>
      )}
      <span>{loading ? 'Connecting...' : 'Continue with Google'}</span>
    </button>
  );
};

const LoginForm = () => {
  useTheme();
  const darkMode = true; // public and account pages always use the site's dark terminal theme
  
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [oauthLoading, setOauthLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Clear all tokens when login page loads
  useEffect(() => {
    console.log('LoginForm mounted - Clearing all stored tokens');
    
    // Clear localStorage
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('session_info');
    localStorage.removeItem('session_expires');
    
    // Clear sessionStorage
    sessionStorage.clear();
    
    // Clear any axios default headers
    if (window.axios && window.axios.defaults && window.axios.defaults.headers) {
      delete window.axios.defaults.headers.common['Authorization'];
    }
  }, []);

  // Check URL parameters for subscription-related messages
  const urlParams = new URLSearchParams(location.search);
  const fromExpired = urlParams.get('from_expired') === 'true';
  const fromLogout = urlParams.get('from_logout') === 'true';
  const subscriptionExpired = urlParams.get('subscription_expired') === 'true';
  const trialExpired = urlParams.get('trial_expired') === 'true';
  const sessionExpired = urlParams.get('session_expired') === 'true';
  const anotherDevice = urlParams.get('reason') === 'another_device';

  // Get subscription status message
  const getSubscriptionMessage = () => {
    if (anotherDevice) {
      return {
        type: 'warning',
        icon: <AlertCircle className="w-5 h-5 text-orange-500" />,
        title: 'Logged Out from Another Device',
        message: 'You have been logged out because you signed in from another location. Please log in again to continue.',
        bgColor: darkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200',
        textColor: darkMode ? 'text-orange-300' : 'text-orange-800'
      };
    }
    
    if (sessionExpired) {
      return {
        type: 'info',
        icon: <Clock className="w-5 h-5 text-blue-500" />,
        title: 'Session Expired',
        message: 'Your session has expired. Please log in again to continue.',
        bgColor: darkMode ? 'bg-[#13283C] border-[#28445F]' : 'bg-blue-50 border-blue-200',
        textColor: darkMode ? 'text-blue-300' : 'text-blue-800'
      };
    }
    
    if (trialExpired) {
      return {
        type: 'warning',
        icon: <Crown className="w-5 h-5 text-orange-500" />,
        title: 'Trial Expired',
        message: 'Your free trial has expired. Please log in to subscribe and continue accessing premium features.',
        bgColor: darkMode ? 'bg-orange-900/20 border-orange-800' : 'bg-orange-50 border-orange-200',
        textColor: darkMode ? 'text-orange-300' : 'text-orange-800',
        showLinks: true
      };
    }
    
    if (subscriptionExpired || fromExpired) {
      return {
        type: 'warning',
        icon: <AlertCircle className="w-5 h-5 text-yellow-500" />,
        title: 'Subscription Expired',
        message: 'Your subscription has expired. Please log in to renew your subscription.',
        bgColor: darkMode ? 'bg-yellow-900/20 border-yellow-800' : 'bg-yellow-50 border-yellow-200',
        textColor: darkMode ? 'text-yellow-300' : 'text-yellow-800',
        showLinks: true,
        showAdminNote: true
      };
    }
    
    if (fromLogout) {
      return {
        type: 'success',
        icon: <CheckCircle className="w-5 h-5 text-green-500" />,
        title: 'Logged Out',
        message: 'You have been successfully logged out.',
        bgColor: darkMode ? 'bg-green-900/20 border-green-800' : 'bg-green-50 border-green-200',
        textColor: darkMode ? 'text-green-300' : 'text-green-800'
      };
    }
    
    return null;
  };

  const subscriptionMessage = getSubscriptionMessage();

  // Handle success message from registration
  useEffect(() => {
    if (location.state?.message) {
      setSuccessMessage(location.state.message);
      if (location.state.email) {
        setFormData(prev => ({ ...prev, username: location.state.email }));
      }
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Handle OAuth errors returned from backend
  // SECURITY FIX #7: Use an allowlist — never render raw URL params into the UI.
  // An attacker could craft /login?error=Your+account+was+hacked.+Call+1-800-...
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const oauthError = urlParams.get('error');

    const OAUTH_ERROR_MESSAGES = {
      google_auth_failed:      'Google authentication failed. Please try again.',
      state_mismatch:          'Security validation failed. Please try again.',
      no_code:                 'Authentication was cancelled. Please try again.',
      token_exchange_failed:   'Failed to complete authentication. Please try again.',
      oauth_callback_failed:   'Authentication failed. Please try again or use email/password.',
    };

    if (oauthError) {
      setError(
        OAUTH_ERROR_MESSAGES[oauthError] ||
        'Authentication failed. Please try again or use email/password.'
      );
      // Clear URL parameters so the error code doesn't linger in history
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.username || !formData.password) {
      setError('Please enter both username/email and password');
      return;
    }
  
    // Clear all tokens BEFORE attempting login
    console.log('Clearing tokens before login attempt');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('session_info');
    localStorage.removeItem('session_expires');
    sessionStorage.clear();
    
    setError('');
    setLoading(true);
  
    try {
      const result = await login(formData.username, formData.password, rememberMe);
      
      console.log('Login successful, tokens should now be set');
      
      // Show success message if user has extended session
      if (result && result.session_info) {
        const sessionDuration = result.session_info.session_duration;
        if (rememberMe && sessionDuration) {
          console.log(`Session extended: You will stay logged in for ${sessionDuration}`);
        }
      }
      
      // Check subscription status and redirect accordingly
      if (result && result.user) {
        const user = result.user;
        
        // SECURITY FIX #2: Never grant privilege based on a username string.
        // currentUser is populated from localStorage which any user can manipulate.
        // Trust only the roles array, which is derived from the server-signed JWT.
        if (user.roles?.includes('admin')) {
          navigate('/live-watch');
          return;
        }
        
        // Check if user has active subscription
        const hasSubscription = user.subscription && user.subscription.status;
        const validStatuses = ['active', 'trialing', 'paid', 'trial'];
        const subscriptionActive = hasSubscription && validStatuses.includes(user.subscription.status);
        
        // Check expiration
        let isExpired = false;
        if (user.subscription && user.subscription.expires_at) {
          const expiresAt = new Date(user.subscription.expires_at);
          const now = new Date();
          isExpired = expiresAt <= now;
          
          console.log('Checking subscription expiration:', {
            expiresAt: expiresAt.toISOString(),
            now: now.toISOString(),
            isExpired
          });
        }
        
        console.log('Subscription validation:', {
          hasSubscription,
          subscriptionActive,
          isExpired,
          status: user.subscription?.status,
          expires_at: user.subscription?.expires_at
        });
        
        // Only redirect to subscription page if NO active subscription OR expired
        if (!subscriptionActive || isExpired) {
          console.log('No active subscription or expired - redirecting to subscription page');
          navigate('/subscription?from=login');
          return;
        }
        
        // Has valid active subscription - go to dashboard
        console.log('Active subscription confirmed - redirecting to dashboard');
        navigate('/live-watch');
        return;
      }
      
      // Fallback: redirect to originally requested page or dashboard.
      // SECURITY FIX #6: Validate the path before using it — only allow same-origin
      // relative paths that start with / and don't start with // (protocol-relative).
      const rawFrom = location.state?.from?.pathname;
      const safePath =
        rawFrom &&
        typeof rawFrom === 'string' &&
        rawFrom.startsWith('/') &&
        !rawFrom.startsWith('//')
          ? rawFrom
          : '/live-watch';
      navigate(safePath);
      
    } catch (err) {
      console.error('Login error:', err);
      
      // Clear tokens on login failure
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('session_info');
      localStorage.removeItem('session_expires');
      
      // Handle specific error cases
      if (err.code === 'SUBSCRIPTION_EXPIRED' || err.code === 'TRIAL_EXPIRED') {
        setError(
          <div>
            <p className="font-semibold mb-2">{err.message}</p>
            {err.is_trial && (
              <p className="text-sm">Your trial expired on {new Date(err.expired_at).toLocaleDateString()}.</p>
            )}
            <div className="mt-3 pt-3 border-t border-red-300 dark:border-red-700">
              <Link 
                to="/subscription" 
                className="text-sm font-medium text-blue-600 hover:text-blue-500 underline"
              >
                View Subscription Plans →
              </Link>
            </div>
          </div>
        );
      } else if (err.code === 'NO_SUBSCRIPTION') {
        setError(
          <div>
            <p className="font-semibold mb-2">{err.message}</p>
            <div className="mt-3 pt-3 border-t border-red-300 dark:border-red-700">
              <Link 
                to="/subscription" 
                className="text-sm font-medium text-blue-600 hover:text-blue-500 underline"
              >
                Subscribe Now →
              </Link>
            </div>
          </div>
        );
      } else if (err.message?.includes('pending approval') || err.message?.includes('not approved')) {
        setError('Your account is pending approval. Please wait for admin confirmation.');
      } else if (err.message?.includes('invalid credentials') || err.message?.includes('Invalid')) {
        setError('Invalid username/email or password. Please check your credentials.');
      } else {
        setError(err.message || 'Login failed. Please check your credentials.');
      }
      
    } finally {
      setLoading(false);
    }
  };

  // Handle Google OAuth authentication
  const handleGoogleAuth = async () => {
    // Clear tokens before OAuth
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('currentUser');
    localStorage.removeItem('session_info');
    localStorage.removeItem('session_expires');
    sessionStorage.clear();
    
    setError('');
    setOauthLoading(true);
    
    try {
      const response = await fetch('/api/auth/google/login', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();
      
      if (response.ok && data.auth_url) {
        // Redirect to Google's OAuth page
        window.location.href = data.auth_url;
      } else {
        throw new Error(data.error || 'Failed to initiate Google authentication');
      }
    } catch (err) {
      console.error('Google auth error:', err);
      setError(err.message || 'Google authentication failed. Please try again.');
      setOauthLoading(false);
    }
  };

  return (
    <div className={`${darkMode ? 'bg-[#0D1E2E] border-[#1C3348]' : 'bg-white border-gray-200'} rounded-md border p-8`}>
      
      {/* Subscription Status Messages */}
      {subscriptionMessage && (
        <div className={`mb-6 p-4 rounded-lg border ${subscriptionMessage.bgColor}`}>
          <div className="flex items-start">
            <div className="mr-3 mt-0.5 flex-shrink-0">
              {subscriptionMessage.icon}
            </div>
            <div className="flex-1">
              <h4 className={`font-medium ${subscriptionMessage.textColor} mb-1`}>
                {subscriptionMessage.title}
              </h4>
              <p className={`text-sm ${subscriptionMessage.textColor} leading-relaxed`}>
                {subscriptionMessage.message}
              </p>
              
              {/* Additional help for subscription-related issues */}
              {subscriptionMessage.showLinks && (
                <div className={`mt-3 pt-3 border-t ${
                  darkMode ? 'border-[#28445F]' : 'border-gray-300'
                }`}>
                  <p className={`text-xs ${
                    darkMode ? 'text-gray-400' : 'text-gray-600'
                  }`}>
                    Need help? <a 
                      href="/contact" 
                      className="text-blue-500 hover:text-blue-600 underline"
                    >
                      Contact Support
                    </a> or view our <a 
                      href="/subscription" 
                      className="text-blue-500 hover:text-blue-600 underline"
                    >
                      Subscription Plans
                    </a>
                  </p>
                </div>
              )}

              {/* Admin note for subscription expiry */}
              {subscriptionMessage.showAdminNote && (
                <div className={`mt-3 pt-3 border-t ${
                  darkMode ? 'border-[#28445F]' : 'border-gray-300'
                }`}>
                  <p className={`text-xs font-medium ${
                    darkMode ? 'text-gray-300' : 'text-gray-700'
                  }`}>
                    Administrators: You can still log in to manage the system. Admin access is not affected by subscription status.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <div className="flex items-center">
            <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
            <div>
              <p className="text-green-800 dark:text-green-200 font-medium">Registration Successful!</p>
              <p className="text-green-700 dark:text-green-300 text-sm mt-1">{successMessage}</p>
              <p className="text-green-600 dark:text-green-400 text-xs mt-2">
                You'll receive an email when your account is approved.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="w-5 h-5 text-red-500 mr-3 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-800 dark:text-red-200 font-medium">Login Failed</p>
              <div className="text-red-700 dark:text-red-300 text-sm mt-1">
                {typeof error === 'string' ? error : error}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google OAuth Button */}
      <div className="mb-6">
        <GoogleAuthButton 
          onClick={handleGoogleAuth}
          loading={oauthLoading}
          disabled={loading}
          darkMode={darkMode}
        />
      </div>

      {/* Divider */}
      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center">
          <div className={`w-full border-t ${darkMode ? 'border-[#28445F]' : 'border-gray-300'}`}></div>
        </div>
        <div className="relative flex justify-center text-sm">
          <span className={`px-4 ${darkMode ? 'bg-[#0D1E2E] text-gray-400' : 'bg-white text-gray-500'}`}>
            Or sign in with email
          </span>
        </div>
      </div>

      {/* Login Form */}
      <form onSubmit={handleSubmit} className="space-y-6">
        
        {/* Username/Email Field */}
        <div className="space-y-1">
          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Username or Email
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type="text"
              name="username"
              value={formData.username}
              onChange={handleChange}
              placeholder="Enter your username or email"
              className={`w-full pl-12 pr-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                darkMode ? 'bg-[#13283C] border-[#28445F] text-white placeholder-gray-400'
                         : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={loading || oauthLoading}
              autoComplete="username"
            />
          </div>
        </div>

        {/* Password Field */}
        <div className="space-y-1">
          <label className={`block text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            Password
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Lock className="w-5 h-5 text-gray-400" />
            </div>
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter your password"
              className={`w-full pl-12 pr-12 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                darkMode ? 'bg-[#13283C] border-[#28445F] text-white placeholder-gray-400'
                         : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
              }`}
              disabled={loading || oauthLoading}
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        {/* Remember Me & Forgot Password */}
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <input
              id="remember-me"
              name="remember-me"
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
            />
            <label htmlFor="remember-me" className={`ml-2 block text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Keep me logged in for 30 days
            </label>
          </div>

          <Link 
            to="/forgot-password" 
            className="text-sm text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Forgot password?
          </Link>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading || oauthLoading || !formData.username || !formData.password}
          className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center space-x-2 ${
            loading || oauthLoading || !formData.username || !formData.password
              ? 'bg-[#1C3348] text-slate-500 cursor-not-allowed'
              : 'bg-[#5AA9F0] text-[#07131F] hover:bg-[#8CC4F5]'
          }`}
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
              <span>Signing in...</span>
            </>
          ) : (
            <>
              <span>Sign in</span>
              <ArrowRight className="w-5 h-5" />
            </>
          )}
        </button>
      </form>

      {/* Registration Section */}
      <div className="text-center mt-6">
        <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'} mb-2`}>
          Don't have an account?
        </p>
        <Link to="/register" className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300 font-medium">
          <span>Create your account</span>
          <ArrowRight className="w-4 h-4" />
        </Link>
      </div>

      {/* Help Section */}
      <div className={`mt-8 p-4 rounded-lg ${darkMode ? 'bg-[#13283C]/30' : 'bg-blue-50/50'}`}>
        <div className="flex items-start space-x-3">
          <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-blue-600' : 'bg-blue-500'}`}>
            <span className="text-white text-xs font-bold">?</span>
          </div>
          <div>
            <h3 className={`text-sm font-medium ${darkMode ? 'text-gray-200' : 'text-gray-900'}`}>
              Need Help?
            </h3>
            <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              If you're having trouble signing in, please contact our support team.
            </p>
            <a 
              href="mailto:support@tunasignal.ai"
              className={`inline-flex items-center space-x-1 text-xs mt-2 ${darkMode ? 'text-blue-400 hover:text-blue-300' : 'text-blue-600 hover:text-blue-500'}`}
            >
              <Mail className="w-3 h-3" />
              <span>support@tunasignal.ai</span>
            </a>
          </div>
        </div>
      </div>

      {/* Footer with Legal Links */}
      <div className={`mt-8 pt-6 border-t ${darkMode ? 'border-[#1C3348]' : 'border-gray-200'}`}>
        <div className="text-center">
          <div className="flex items-center justify-center space-x-3">
            <Link 
              to="/legal#privacy" 
              className={`text-xs ${
                darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
              } transition-colors`}
            >
              Privacy Policy
            </Link>
            <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>|</span>
            <Link 
              to="/legal#terms" 
              className={`text-xs ${
                darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
              } transition-colors`}
            >
              Terms & Conditions
            </Link>
            <span className={`text-xs ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}>|</span>
            <Link 
              to="/legal#refund" 
              className={`text-xs ${
                darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-600 hover:text-gray-800'
              } transition-colors`}
            >
              Refund Policy
            </Link>
          </div>
          <p className={`text-xs mt-3 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            © 2025 TunaSignal AI. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;